if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        this.qtyInput = this.querySelector('[data-sticky-atc-qty-input]');
        this.qtyMinus = this.querySelector('[data-sticky-atc-qty-minus]');
        this.qtyPlus = this.querySelector('[data-sticky-atc-qty-plus]');
        this.submitButton = this.querySelector('[data-sticky-atc-submit]');
        this.submitText = this.querySelector('[data-sticky-atc-submit-text]');
        this.priceEl = this.querySelector('[data-sticky-atc-price]');
        this.comparePriceEl = this.querySelector('[data-sticky-atc-compare-price]');
        this.imageEl = this.querySelector('[data-sticky-atc-image]');

        this.bindQuantity();
        this.bindSubmit();
        this.bindVariantSync();
        this.bindVisibilityToggle();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        this.visibilityObserver?.disconnect();
      }

      // ---------- Quantity (local UI only — synced into the real
      // form's quantity field at submit time) ----------

      bindQuantity() {
        if (!this.qtyInput) return;

        const step = () => parseInt(this.qtyInput.step, 10) || 1;
        const min = () => (this.qtyInput.min !== '' ? parseInt(this.qtyInput.min, 10) : 1);
        const max = () => (this.qtyInput.max !== '' ? parseInt(this.qtyInput.max, 10) : null);

        this.qtyMinus?.addEventListener('click', () => {
          const current = parseInt(this.qtyInput.value, 10) || min();
          const next = Math.max(min(), current - step());
          this.qtyInput.value = next;
        });

        this.qtyPlus?.addEventListener('click', () => {
          const current = parseInt(this.qtyInput.value, 10) || min();
          const ceiling = max();
          const next = ceiling !== null ? Math.min(ceiling, current + step()) : current + step();
          this.qtyInput.value = next;
        });
      }

      updateQuantityBoundaries() {
        if (!this.qtyInput) return;
        this.qtyInput.min = this.dataset.min || 1;
        if (this.dataset.max) {
          this.qtyInput.max = this.dataset.max;
        } else {
          this.qtyInput.removeAttribute('max');
        }
        this.qtyInput.step = this.dataset.step || 1;

        const min = parseInt(this.qtyInput.min, 10) || 1;
        if (parseInt(this.qtyInput.value, 10) < min) this.qtyInput.value = min;
      }

      // ---------- Submit: find the real native form and trigger it ----------

      get mainForm() {
        return document.querySelector('form[data-type="add-to-cart-form"]');
      }

      bindSubmit() {
        this.submitButton?.addEventListener('click', () => {
          const form = this.mainForm;
          if (!form) {
            console.warn('Sticky add to cart: no native product form found on this page.');
            return;
          }

          // Push our local quantity value into the real form.
          const quantity = this.qtyInput ? parseInt(this.qtyInput.value, 10) || 1 : 1;
          let quantityField = form.querySelector('[name="quantity"]');
          if (!quantityField) {
            quantityField = document.createElement('input');
            quantityField.type = 'hidden';
            quantityField.name = 'quantity';
            form.appendChild(quantityField);
          }
          quantityField.value = quantity;

          // Native submission — same code path as clicking the
          // on-page Add to Cart button. No fetch, no custom logic.
          form.requestSubmit();
        });
      }

      // ---------- Variant sync via the theme's existing pub/sub ----------

      bindVariantSync() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          const variant = data?.variant;
          if (!variant) {
            this.setUnavailable();
            return;
          }
          this.renderVariant(variant);
        });
      }

      renderVariant(variant) {
        if (this.priceEl) {
          this.priceEl.textContent = formatMoney(variant.price, window.STICKY_ATC_MONEY_FORMAT);
        }

        if (this.comparePriceEl) {
          const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
          if (onSale) {
            this.comparePriceEl.textContent = formatMoney(variant.compare_at_price, window.STICKY_ATC_MONEY_FORMAT);
            this.comparePriceEl.hidden = false;
          } else {
            this.comparePriceEl.textContent = '';
            this.comparePriceEl.hidden = true;
          }
        }

        if (this.imageEl && variant.featured_image?.src) {
          this.imageEl.src = variant.featured_image.src.replace(/(\.[a-zA-Z]{3,4})(\?|$)/, '_200x$1$2');
          this.imageEl.alt = variant.featured_image.alt || '';
        }

        if (variant.quantity_rule) {
          this.dataset.min = variant.quantity_rule.min ?? 1;
          this.dataset.step = variant.quantity_rule.increment ?? 1;
          if (variant.quantity_rule.max != null) {
            this.dataset.max = variant.quantity_rule.max;
          } else {
            delete this.dataset.max;
          }
          this.updateQuantityBoundaries();
        }

        if (variant.available) {
          this.submitButton.disabled = false;
          if (this.submitText) this.submitText.textContent = window.variantStrings?.addToCart || 'Add to cart';
        } else {
          this.submitButton.disabled = true;
          if (this.submitText) this.submitText.textContent = window.variantStrings?.soldOut || 'Sold out';
        }
      }

      setUnavailable() {
        if (this.submitButton) this.submitButton.disabled = true;
        if (this.submitText) this.submitText.textContent = window.variantStrings?.unavailable || 'Unavailable';
      }

      // ---------- Show/hide based on whether the real Add to Cart
      // button is currently on screen ----------

      bindVisibilityToggle() {
        const form = this.mainForm;
        const realButton = form?.querySelector('[type="submit"]');
        if (!realButton || typeof IntersectionObserver === 'undefined') {
          this.classList.add('is-visible');
          return;
        }

        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            this.classList.toggle('is-visible', !entry.isIntersecting);
          },
          { threshold: 0 }
        );
        this.visibilityObserver.observe(realButton);
      }
    }
  );
}

// Minimal, self-contained Shopify money formatter (supports the
// standard {{amount}} tokens). No dependency on other theme assets.
function formatMoney(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');

  function defaultOption(opt, def) {
    return typeof opt === 'undefined' ? def : opt;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ',');
    decimal = defaultOption(decimal, '.');

    if (isNaN(number) || number == null) return 0;

    number = (number / 100.0).toFixed(precision);

    const parts = number.split('.');
    const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  const formatString = format || '${{amount}}';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  let value = '';

  switch (formatString.match(placeholderRegex)?.[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    default:
      value = formatWithDelimiters(cents, 2);
      break;
  }

  return formatString.replace(placeholderRegex, value);
}